import { doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Busca todas as roles de um usuário
 * @param {string} userId - ID do usuário
 * @returns {Promise<object>} - Objeto com as roles do usuário
 */
export const getUserRoles = async (userId) => {
  const docRef = doc(db, 'userRoles', userId);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return docSnap.data();
  }
  
  return { salons: [] };
};

/**
 * Verifica se o usuário é super admin
 * @param {string} userId - ID do usuário
 * @returns {Promise<boolean>} - True se o usuário for super admin
 */
export const isSuperAdmin = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  
  if (userSnap.exists()) {
    return userSnap.data().isSuperAdmin === true;
  }
  
  return false;
};

/**
 * Busca todos os salões que um usuário tem acesso
 * @param {string} userId - ID do usuário
 * @returns {Promise<Array>} - Array com os salões que o usuário tem acesso
 */
export const getUserSalons = async (userId) => {
  // Verifica se é super admin (acesso a todos os salões)
  const superAdmin = await isSuperAdmin(userId);
  
  if (superAdmin) {
    // Buscar todos os salões
    const salonsRef = collection(db, 'salons');
    const salonsSnap = await getDocs(salonsRef);
    return salonsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      role: 'admin'
    }));
  }
  
  // Buscar roles do usuário
  const userRoles = await getUserRoles(userId);
  
  if (!userRoles.salons || userRoles.salons.length === 0) {
    return [];
  }
  
  // Buscar detalhes de cada salão
  const salonsDetails = await Promise.all(
    userRoles.salons.map(async (salonRole) => {
      try {
        const salonRef = doc(db, 'salons', salonRole.salonId);
        const salonSnap = await getDoc(salonRef);
        
        if (salonSnap.exists()) {
          // Se for um profissional, verificar se tem professionalId
          if (salonRole.role === 'professional' && !salonRole.professionalId) {
            console.warn(`[getUserSalons] Profissional sem professionalId para usuário ${userId} no salão ${salonRole.salonId}`);
            
            // Tentar encontrar o profissional pelo userId
            try {
              const professionalsRef = collection(db, `salons/${salonRole.salonId}/professionals`);
              const professionalsQuery = query(professionalsRef, where('userId', '==', userId));
              const professionalsSnap = await getDocs(professionalsQuery);
              
              if (!professionalsSnap.empty) {
                const professionalDoc = professionalsSnap.docs[0];
                const professionalId = professionalDoc.id;
                
                console.log(`[getUserSalons] Encontrado professionalId ${professionalId} para usuário ${userId}`);
                
                // Atualizar o documento userRoles com o professionalId encontrado
                const userRolesRef = doc(db, 'userRoles', userId);
                await updateDoc(userRolesRef, {
                  salons: userRoles.salons.map(s => 
                    s.salonId === salonRole.salonId 
                      ? { ...s, professionalId } 
                      : s
                  )
                });
                
                // Atualizar o objeto local também
                salonRole.professionalId = professionalId;
              } else {
                console.error(`[getUserSalons] Não foi possível encontrar o profissional para o usuário ${userId} no salão ${salonRole.salonId}`);
              }
            } catch (err) {
              console.error(`[getUserSalons] Erro ao buscar profissional por userId:`, err);
            }
          }
          
          return {
            id: salonSnap.id,
            ...salonSnap.data(),
            role: salonRole.role,
            permissions: salonRole.permissions,
            professionalId: salonRole.professionalId
          };
        }
        
        return null;
      } catch (err) {
        console.error(`[getUserSalons] Erro ao buscar detalhes do salão ${salonRole.salonId}:`, err);
        return null;
      }
    })
  );
  
  return salonsDetails.filter(salon => salon !== null);
};

/**
 * Verifica se o usuário tem permissão em um salão
 * @param {string} userId - ID do usuário
 * @param {string} salonId - ID do salão
 * @param {string} permission - Permissão a ser verificada
 * @returns {Promise<boolean>} - True se o usuário tiver a permissão
 */
export const checkUserPermission = async (userId, salonId, permission) => {
  // Super admins têm todas as permissões
  if (await isSuperAdmin(userId)) {
    return true;
  }
  
  const roles = await getUserRoles(userId);
  const salonRole = roles.salons.find(salon => salon.salonId === salonId);
  
  if (!salonRole) return false;
  
  // Proprietários e administradores têm todas as permissões
  if (salonRole.role === 'owner' || salonRole.role === 'admin') return true;
  
  // Verificar permissões específicas para gestores
  if (salonRole.role === 'manager') {
    return salonRole.permissions.includes(permission);
  }
  
  // Verificar permissões para profissionais (limitadas)
  if (salonRole.role === 'professional') {
    const professionalPermissions = ['view_own_schedule', 'view_own_commissions', 'manage_own_profile'];
    return professionalPermissions.includes(permission);
  }
  
  return false;
};

/**
 * Adiciona um usuário a um salão com uma role específica
 * @param {string} userId - ID do usuário
 * @param {string} salonId - ID do salão
 * @param {string} role - Role do usuário no salão
 * @param {Array} permissions - Array de permissões
 * @param {string} professionalId - ID do profissional (se a role for 'professional')
 * @returns {Promise<boolean>} - True se a operação for bem-sucedida
 */
export const addUserToSalon = async (userId, salonId, role, permissions = [], professionalId = null) => {
  const docRef = doc(db, 'userRoles', userId);
  const docSnap = await getDoc(docRef);
  
  const salonRole = {
    salonId,
    role,
    permissions: (role === 'owner' || role === 'admin') ? ['all'] : permissions,
    ...(professionalId && { professionalId })
  };
  
  if (docSnap.exists()) {
    // Remover entrada anterior para este salão, se existir
    const roles = docSnap.data();
    const updatedSalons = roles.salons.filter(salon => salon.salonId !== salonId);
    
    // Adicionar nova entrada
    await updateDoc(docRef, {
      salons: [...updatedSalons, salonRole],
      updatedAt: new Date()
    });
  } else {
    // Criar novo documento
    await setDoc(docRef, {
      salons: [salonRole],
      createdAt: new Date(),
      updatedAt: new Date()
    });
  }
  
  // Atualizar documento do salão
  const salonRef = doc(db, 'salons', salonId);
  const salonSnap = await getDoc(salonRef);
  
  if (salonSnap.exists()) {
    if (role === 'owner') {
      await updateDoc(salonRef, {
        owners: arrayUnion(userId)
      });
    } else if (role === 'manager') {
      await updateDoc(salonRef, {
        managers: arrayUnion(userId)
      });
    } else if (role === 'admin') {
      await updateDoc(salonRef, {
        admins: arrayUnion(userId)
      });
    }
  } else {
    // Criar novo documento do salão se não existir
    await setDoc(salonRef, {
      owners: role === 'owner' ? [userId] : [],
      managers: role === 'manager' ? [userId] : [],
      admins: role === 'admin' ? [userId] : [],
      createdAt: new Date(),
      updatedAt: new Date()
    });
  }
  
  return true;
};

/**
 * Remove um usuário de um salão
 * @param {string} userId - ID do usuário
 * @param {string} salonId - ID do salão
 * @returns {Promise<boolean>} - True se a operação for bem-sucedida
 */
export const removeUserFromSalon = async (userId, salonId) => {
  const userRolesRef = doc(db, 'userRoles', userId);
  const userRolesSnap = await getDoc(userRolesRef);
  
  if (!userRolesSnap.exists()) {
    return false;
  }
  
  const userData = userRolesSnap.data();
  const userRole = userData.salons.find(salon => salon.salonId === salonId);
  
  if (!userRole) {
    return false;
  }
  
  // Atualizar userRoles
  const updatedSalons = userData.salons.filter(salon => salon.salonId !== salonId);
  await updateDoc(userRolesRef, {
    salons: updatedSalons,
    updatedAt: new Date()
  });
  
  // Atualizar documento do salão
  const salonRef = doc(db, 'salons', salonId);
  const role = userRole.role;
  
  if (role === 'owner') {
    await updateDoc(salonRef, {
      owners: arrayRemove(userId)
    });
  } else if (role === 'manager') {
    await updateDoc(salonRef, {
      managers: arrayRemove(userId)
    });
  } else if (role === 'admin') {
    await updateDoc(salonRef, {
      admins: arrayRemove(userId)
    });
  }
  
  return true;
};

/**
 * Atualiza as permissões de um usuário em um salão
 * @param {string} userId - ID do usuário
 * @param {string} salonId - ID do salão
 * @param {Array} permissions - Novas permissões
 * @returns {Promise<boolean>} - True se a operação for bem-sucedida
 */
export const updateUserPermissions = async (userId, salonId, permissions) => {
  const userRolesRef = doc(db, 'userRoles', userId);
  const userRolesSnap = await getDoc(userRolesRef);
  
  if (!userRolesSnap.exists()) {
    return false;
  }
  
  const userData = userRolesSnap.data();
  const salonIndex = userData.salons.findIndex(salon => salon.salonId === salonId);
  
  if (salonIndex === -1) {
    return false;
  }
  
  // Não permitir alteração de permissões para proprietários ou administradores
  if (['owner', 'admin'].includes(userData.salons[salonIndex].role)) {
    return false;
  }
  
  // Atualizar permissões
  const updatedSalons = [...userData.salons];
  updatedSalons[salonIndex] = {
    ...updatedSalons[salonIndex],
    permissions
  };
  
  await updateDoc(userRolesRef, {
    salons: updatedSalons,
    updatedAt: new Date()
  });
  
  return true;
};

/**
 * Define um usuário como super admin
 * @param {string} userId - ID do usuário
 * @param {boolean} isAdmin - Se o usuário é super admin
 * @returns {Promise<boolean>} - True se a operação for bem-sucedida
 */
export const setSuperAdmin = async (userId, isAdmin = true) => {
  try {
    const userRef = doc(db, 'users', userId);
    
    await updateDoc(userRef, {
      isSuperAdmin: isAdmin,
      updatedAt: new Date()
    });
    
    return true;
  } catch (error) {
    console.error('Erro ao definir super admin:', error);
    return false;
  }
};

/**
 * Verifica se o usuário tem permissão para gerenciar usuários (convites e permissões)
 * @param {string} userId - ID do usuário
 * @param {string} salonId - ID do salão
 * @returns {Promise<boolean>} - True se o usuário puder gerenciar usuários
 */
export const canUserManageUsers = async (userId, salonId) => {
  // Super admins podem sempre gerenciar usuários
  if (await isSuperAdmin(userId)) {
    return true;
  }
  
  // Verificar papéis do usuário neste salão
  const roles = await getUserRoles(userId);
  const salonRole = roles.salons && roles.salons.find(salon => salon.salonId === salonId);
  
  if (!salonRole) return false;
  
  // Proprietários e administradores têm permissão para gerenciar usuários
  if (salonRole.role === 'owner' || salonRole.role === 'admin') {
    return true;
  }
  
  // Verificar permissões para gerentes
  if (salonRole.role === 'manager') {
    // Verificar se tem permissão específica
    return salonRole.permissions.includes('manage_users') || 
           salonRole.permissions.includes('manage_salon_settings') ||
           salonRole.permissions.includes('all');
  }
  
  return false;
}; 